import IntlMessageFormat from "intl-messageformat";

import { catalogs, type SupportedLocale } from "../locales/catalog-registry";

/** Resolves catalog keys to localized strings with optional ICU placeholders. */
export type Translate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

/**
 * Builds a translator for the active locale using ICU MessageFormat interpolation.
 *
 * @param locale - Selected UI locale.
 * @returns Function that resolves catalog keys to localized strings.
 */
export function createTranslator(locale: SupportedLocale): Translate {
  const bundle = catalogs[locale];
  return (key, values) => {
    const pattern = bundle[key];
    if (pattern === undefined) {
      throw new Error(`missing catalog entry for ${key} (${locale})`);
    }
    // ICU treats `{}` (optional spaces) as an empty placeholder and throws **EMPTY_ARGUMENT**.
    if (/^\{\s*\}$/.test(pattern.trim())) {
      return "{}";
    }
    return new IntlMessageFormat(pattern, locale).format(
      values ?? {},
    ) as string;
  };
}

/**
 * Maps a browser language tag to a supported locale.
 *
 * @param navLang - Value such as `navigator.language`.
 */
export function resolveLocale(navLang: string | undefined): SupportedLocale {
  const raw = navLang?.split("-")[0]?.toLowerCase();
  if (raw === "de" || raw === "fr" || raw === "da" || raw === "uk") {
    return raw;
  }
  return "en";
}
